import React from "react";
import styled from "styled-components";
import { Button } from "@material-ui/core";
import { useTranslation } from "react-i18next";

import { getStaticUrl } from "js/config";
import Container from "js/react/views/MarketingSurvey/Components/Container";
import BeautifulDialog from "js/react/components/Dialogs/BeautifulDialog";
import { WorkspacePlanTier } from "common/interfaces";
import { trackActivity } from "js/core/utilities/utilities";

const blueCheckIcon = getStaticUrl("/images/onboarding/check.png");

const StyledDialogTitle = styled.div`
    text-align: center;
    color: #222;
    font-family: "Source Sans Pro";
    font-size: 20px;
    font-style: normal;
    font-weight: 600;
    line-height: 145%;
    letter-spacing: -0.26px;
    margin-top: 40px;
    padding: 0 20px;

    @media (min-width: 768px) {
        font-size: 26px;
        margin-top: 102px;
        padding: 0;
    }
`;

const StyledDialogContent = styled.div`
    display: flex;
    flex-direction: row;
    justify-content: center;
    gap: 39px;

    @media (max-width: 768px) {
        flex-direction: column;
        gap: 20px;
        padding: 0 20px;
        align-items: center;
    }
`;

const PlanBox = styled.div`
    display: flex;
    width: 100%;
    max-width: 322px;
    height: auto;
    padding: 30px 24px;
    flex-direction: column;
    align-items: flex-start;
    border-radius: 4px;
    border: 1px solid #EEE;
    margin-top: 20px;

    @media (min-width: 768px) {
        width: 322px;
        padding: 40px 48px;
        margin-top: 39px;
    }
`;

const PlanRow = styled.div`
    display: flex;
    justify-content: space-between;
    flex-direction: row;
    align-items: center;
    align-self: stretch;
`;

const PlanName = styled.div`
    color: #000;
    font-family: "Source Sans Pro";
    font-size: 24px;
    font-style: normal;
    font-weight: 400;
    line-height: 120%;
    letter-spacing: -0.48px;
`;

const PlanIntent = styled.div`
    display: flex;
    padding: 3px 8px;
    align-items: flex-start;
    gap: 10px;
    border-radius: 1px;
    background: rgba(17, 169, 226, 0.15);
    color: #11A9E2;
    font-family: "Source Sans Pro";
    font-size: 14px;
    font-style: normal;
    font-weight: 600;
    line-height: 110%;
`;

const PlanPrice = styled.div`
    color: #000;
    font-family: "Source Sans Pro";
    font-size: 36px;
    font-style: normal;
    font-weight: 600;
    line-height: 120%;
    letter-spacing: -0.96px;
    margin-top: 14px;
    margin-bottom: 20px;
`;

const PaddedButton = styled(Button)`
    && {
        display: flex;
        padding: 10px 30px;
        justify-content: center;
        align-items: center;
        gap: 10px;
        align-self: stretch;
        border-radius: 1px;
        background: #11A9E2;
        color: #FFF;
        text-align: center;
        font-family: "Source Sans Pro";
        font-size: 14px;
        font-style: normal;
        font-weight: 600;
        line-height: normal;
        letter-spacing: 0.5px;
        text-transform: uppercase;
        margin-bottom: 40px;

        &:hover {
            background-color: #2980b9;
        }
    }
`;

const PlanMore = styled.div`
    fcolor: #000;
    font-family: "Source Sans Pro";
    font-size: 16px;
    font-style: normal;
    font-weight: 400;
    line-height: 125%;
    letter-spacing: 0.5px;
    white-space: nowrap;
    margin-bottom: 16px;
`;

const FeatureList = styled.ul`
    list-style-type: none;
    padding: 0;
    margin: 0;
    align-self: flex-start;
    width: 100%;

    li {
        margin-bottom: 16px;
        display: flex;
        align-items: center;
        color: #000;
        font-family: "Source Sans Pro";
        font-size: 14px;
        font-style: normal;
        font-weight: 400;
        line-height: 125%;
        letter-spacing: 0.5px;

        @media (min-width: 768px) {
            font-size: 16px;
            white-space: nowrap;
        }

        &::before {
            content: "";
            display: inline-block;
            width: 16px;
            height: 16px;
            margin-right: 10px;
            background-image: url(${blueCheckIcon});
            background-size: contain;
            background-repeat: no-repeat;
            flex-shrink: 0;
        }
    }
`;

const HiddenButton = styled.div`
    display: block;
    position: absolute;
    bottom: 10px;
    left: 10px;
    width: 3px;
    height: 3px;
    opacity: 0;
`;

export default function ChoosePlan({ handlePlanSelected }: { handlePlanSelected: (plan: WorkspacePlanTier) => void }) {
    const { t } = useTranslation();

    return ( // @ts-ignore
        <Container prevProgress={64} progress={80}>
            <BeautifulDialog
                PaperProps={{
                    style: {
                        backgroundColor: "transparent",
                        boxShadow: "none",
                        maxWidth: 800
                    },
                }}
                BackdropProps={{
                    style: {
                        backgroundColor: "transparent",
                    },
                }}
                preventClose={true}
                style={{
                    overflowY: "auto"
                }}
            >
                <StyledDialogTitle>
                    {t("Choose the plan that's right for you")}
                </StyledDialogTitle>
                <StyledDialogContent>
                    <PlanBox>
                        <PlanRow>
                            <PlanName>{t("Pro")}</PlanName>
                            <PlanIntent>{t("FOR INDIVIDUALS")}</PlanIntent>
                        </PlanRow>
                        <PlanPrice>{t("$12/mo.")}</PlanPrice>
                        <PaddedButton
                            id="choose-pro"
                            variant="contained"
                            color="primary"
                            onClick={() => handlePlanSelected(WorkspacePlanTier.PRO)}
                        >
                            {t("TRY IT FREE")}
                        </PaddedButton>
                        <PlanMore>{t("Everything for professionals")}:</PlanMore>
                        <FeatureList>
                            <li>{t("Version History")}</li>
                            <li>{t("Powerpoint Export")}</li>
                            <li>{t("Viewer Analytics")}</li>
                        </FeatureList>
                    </PlanBox>
                    <PlanBox>
                        <PlanRow>
                            <PlanName>{t("Team")}</PlanName>
                            <PlanIntent>{t("FOR COMPANIES")}</PlanIntent>
                        </PlanRow>
                        <PlanPrice>{t("$40/mo.")}</PlanPrice>
                        <PaddedButton
                            id="choose-team"
                            variant="contained"
                            color="primary"
                            onClick={() => handlePlanSelected(WorkspacePlanTier.TEAM)}
                        >
                            {t("TRY IT FREE")}
                        </PaddedButton>
                        <PlanMore>{t("Everything in Pro, plus")}:</PlanMore>
                        <FeatureList>
                            <li>{t("Collaborative Workspace")}</li>
                            <li>{t("Custom Company Theme")}</li>
                            <li>{t("PowerPoint Import")}</li>
                            <li>{t("Slide and Template Library")}</li>
                        </FeatureList>
                    </PlanBox>
                    <HiddenButton
                        id="choose-basic"
                        onClick={() => handlePlanSelected(WorkspacePlanTier.BASIC)}
                    />
                </StyledDialogContent>
            </BeautifulDialog>
        </Container>
    );
}

export function trackAnalytics(plan: WorkspacePlanTier) {
    trackActivity("SignupFlow", "NavForward", null, null, {
        step_number: 7,
        step_name: "Choose Plan",
        plan_selected: plan,
        experiment_group_assignment: "B",
        object: "button",
        object_label: plan.toUpperCase(),
        action: "clicked",
        workspace_id: "all"
    }, { audit: true });
}
